import Vue from 'vue'
import moment from 'moment-timezone'
import numbro from 'numbro'
import Big from 'big.js'
import store from '@store/index'
import {_CONST} from "@/constants/constants";
import {getPrecision} from "@services/helpers";
import {translateFunction} from '@/translate'

// filters
import numeral from './numeral';

const getSymbols = () => {
  return store.getters['main/getSymbols'];
};

const getCurrencies = () => {
  return {
    ...store.getters['main/getCurrencies'],
    USD: _CONST.USD
  }
};

const DEFAULT_TIMEZONE = 'Europe/Moscow';

export const _FILTER = {
  precision(value, mantissa = 4, trimMantissa = false, rounding = true) {
    if (!value) return value;
    if (typeof value === "string") value = +value.replace(',','.');
    return numbro(value).format({
      roundingFunction: rounding ? Math.round : Math.floor,
      trimMantissa,
      mantissa
    });
  },
  moment(value, {format = 'YYYY-MM-DD, HH:mm:ss', tz = _CONST.timeZoneType.server} = {}) {
    if (!value) return value;

    let m = moment(value);

    switch (tz) {
      case _CONST.timeZoneType.server:
        m = moment(value).utcOffset(value);
        m.tz(DEFAULT_TIMEZONE);
        break;

      case _CONST.timeZoneType.local:
        m = moment(value);
        break;
    }

    return m.format(format);
  },
  momentHumanizedFromNow(value) {
    if (!value) return value;
    return moment(value).fromNow();
  },
  averageTime(value) {
    if (!value) return value;

    let [v1,v2] = ('' +value).split('.');
    if(v2) return v1 + '.' + v2.split('0')[0];

    return v1;
  },

  big(value, precision) {
    if (!value || isNaN(value)) return value;

    let big = new Big(value);

    return precision ? big.toFixed(precision) : big.toFixed();
  },
  stepPrecision(value, symbol) {
    if (value === null || isNaN(value)) return value;

    let bVal = new Big(+value);
    if (!symbol) return bVal.toFixed();

    if (!(symbol.tradeStep > 0)) return bVal.toFixed();

    const stepPrecision = getPrecision(symbol.tradeStep);

    let bStep = new Big(symbol.tradeStep);
    let bMin = new Big(symbol.minTradeAmount);
    let bInterval = bVal.minus(bMin);

    let bCount = bInterval.div(bStep).round(0);

    let bResult = bStep.times(bCount).plus(bMin);
    if ((bVal.minus(bResult)).gt(bStep.div(2))) {
      bResult = bResult.plus(bStep);
    }

    return bResult.toFixed(stepPrecision);
  },
  smartPrecision(value, options = {}) {
    let {
      symbol,
      symbolName,
      currency,
      currencyName,
      type,
      precision = 2,
      isPretty = false,
      trimMantissa = false,
      isRoundFloor = false,
      isOverflowRound = false,
      isOverflowDefaultRound = false,
      isShowConsole = true
    } = options
    if (value === null || isNaN(+value)) return value;
    value = +value;
    if (!type) type = (precision >= 0) ? _CONST.precisionType.precision : _CONST.precisionType.symbol;
    if (!symbol && symbolName) symbol = getSymbols()[symbolName];
    let big = new Big(value);
    const currentPrecision = getPrecision(value);
    const returnValue = (val, precisionRes) => {
      return (isPretty ? Vue.options.filters.pretty(val, {precisionRes}) : val)
    }

    const returnValueWith = precisionRes => {
      if (currentPrecision <= precisionRes) {
        return returnValue(_FILTER.precision(big.toFixed(), precisionRes, trimMantissa), precisionRes)
      }

      if (isShowConsole) {
        console.warn('>> Precision overflow!', `Value=${value}; currentPrecision=${currentPrecision}; needlePrecision=${precisionRes}`)
      }

      if (isOverflowRound) {
        return returnValue(_FILTER.precision(big.toFixed(), precisionRes, trimMantissa, !isRoundFloor), precisionRes)
      }
      if (isOverflowDefaultRound) {
        return returnValue(_FILTER.precision(big.toFixed(), _CONST.defaultPrecision, trimMantissa, !isRoundFloor), _CONST.defaultPrecision)
      }

      return returnValue(big.toFixed())
    };

    switch (type) {
      case _CONST.precisionType.precision:
        if (precision >= 0) return returnValueWith(precision);
        break;

      case _CONST.precisionType.symbol:
        return returnValueWith(symbol.precision);

      case _CONST.precisionType.base:
        currency = getCurrencies()[symbol.baseCurrency];
        if (currency) return returnValueWith(currency.precision);
        break;

      case _CONST.precisionType.quoted:
        currency = getCurrencies()[symbol.quotedCurrency];
        if (currency) return returnValueWith(currency.precision);
        break;

      case _CONST.precisionType.currency:
        if (!currency) currency = getCurrencies()[currencyName];
        if (currency) return returnValueWith(currency.precision);
        break;

      case _CONST.precisionType.step:
        return returnValue(_FILTER.stepPrecision(+value, symbol))
    }

    return returnValue(big.toFixed())
  },

  textMore(value, length = 5, endLength = 0, isShowDots = true) {
    if (!value) return value;

    if (value.length <= length + endLength) return  value;

    let result = value.substring(0, length - 1) + (isShowDots ? '...' : '');

    if (endLength > 0) result += value.substr(-endLength);

    return result;
  },
  yesNo(value) {
    return value ? 'Да' : 'Нет';
  },
  yesNoActive(value) {
    return value ? 'Активен' : 'Неактивен';
  },
  blank(value, isWithZero = true) {
    if (isWithZero) if (value === 0) return value;
    return value ? value : '-';
  }
}

Vue.filter('gatewayLabel', (gatewayId = '', { view } = { view: 'full' }) => {
  const regex = /^([^_]+)_(\w+)$/i

  const gatewayName = regex.test(gatewayId)
    ? regex.exec(gatewayId)[2]
    : gatewayId

  const isRubLabel = !!_CONST.gatewayLabelRub[gatewayName]

  if (isRubLabel) {
    return translateFunction(`common.gatewayLabelRub.${gatewayName}`) || _CONST.gatewayLabelRub[gatewayName]
  } else {
    const name = _CONST.gatewayLabelName[gatewayName] || gatewayName.toUpperCase()
    const desc = _CONST.gatewayLabelDesc[gatewayName]
      ? translateFunction(`common.gatewayLabelDesc.${gatewayName}`) || _CONST.gatewayLabelDesc[gatewayName]
      : gatewayName

    switch (view) {
      case 'short':
        return name
      case 'desc':
        return desc
      case 'full':
      default:
        return `${name} (${desc})`
    }
  }
});

Vue.filter('pretty', (val, options = {}) => {
  if (val < 1000) return val;
  val = Vue.options.filters.big(val);
  let {mask = '0,0.[000000000000000000000]', blank = 0, precision = 0} = options;
  if (precision > 0) {
    const zeroPRecision = '0'.repeat(precision)
    mask = `0,0.${zeroPRecision}[000000000000000000000]`
  }
  let res = numeral(val, mask);
  res = res.replaceAll(',', ' ')
  return res || blank;
});

Vue.filter('expoNormalize', (value = 0) => {
  let data = String(value).split(/[eE]/);
  if (data.length === 1) return data[0];

  let z = '',
      sign = value < 0 ? '-' : '',
      str = data[0].replace('.', ''),
      mag = Number(data[1]) + 1;

  if (mag < 0) {
    z = sign + '0.';
    while (mag++) z += '0';
    return z + str.replace(/^\-/, '');
  }
  mag -= str.length;
  while (mag--) z += '0';
  return str + z;
});

Vue.filter('precision', _FILTER.precision);
Vue.filter('moment', _FILTER.moment);
Vue.filter('momentHumanizedFromNow', _FILTER.momentHumanizedFromNow);
Vue.filter('averageTime', _FILTER.averageTime);

Vue.filter('big', _FILTER.big);
Vue.filter('stepPrecision', _FILTER.stepPrecision);
Vue.filter('smartPrecision', _FILTER.smartPrecision);

Vue.filter('textMore', _FILTER.textMore);
Vue.filter('yesNo', _FILTER.yesNo);
Vue.filter('yesNoActive', _FILTER.yesNoActive);
Vue.filter('blank', _FILTER.blank);
